import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import * as Sentry from '@sentry/react';

// Offline queue
import {
  getCompoundQueue,
  removeCompoundOperation,
  updateCompoundStatus,
  hasExceededMaxRetries,
  resetExhaustedCompoundOperations,
  markCompoundPhotosHandedOff,
  type OfflineCompoundOperation,
} from './offlineCompoundQueue';
import {
  createMemoryFormStore,
  createMemoryImageSource,
  type FormContext,
} from './formStorage';
import { classifyOfflineError } from './classifyOfflineError';
import { isSessionChangedError } from '../gql/refreshFailure';
import type { PhotoHandoff } from './photoHandoff';
import {
  createPhaseDeadline,
  isTransportFailure,
  phaseScopedClient,
} from './phase2Transport';
import { isOnlineNow } from './connectivity';
import { belongsToCurrentSession, currentQueueOwner, sessionLeftOperation } from './queueOwner';
import {
  prependActionToCache,
  prependCampaignToCache,
  prependCommunicationToCache,
  prependGenericOrderToCache,
} from '../gql/cacheHelpers';

// GraphQL mutations
// C-11.2: cascada legacy CREATE_NEW_ORDER + CREATE_NEW_ACTION + UPDATE_ORDER +
// UPDATE_ORDER_RELATIONSHIPS reemplazada por SAVE_ACTION atómica. Elimina el
// `Promise.all` paralelo que era patrón clásico P2034 latente.
import { SAVE_ACTION } from '../gql/mutations/actions';
import { SAVE_COMMUNICATION } from '../gql/mutations/communication';
import { SAVE_GENERIC_ORDER } from '../gql/mutations/generic-order';
import { SAVE_CAMPAIGN } from '../gql/mutations/campaign';
import { LINK_ORDER_TO_SEGMENT } from '../gql/mutations/surveillanceRoute';

// Utils
import {
  compressSessionStorage,
  decompressSessionStorage,
} from '../utils/sessionStorage';
import {
  formActionNaming,
} from '../components/forms/ActionForms/constants';
import {
  formAccidentNaming,
} from '../components/forms/AddBasicInfoAccidentForm/constants';
import { formVehicleNaming } from '../components/forms/AddVehicleForm/constants';
import { buildSaveActionInputFromForm } from './saveActionInput';
import { hasSyntheticId } from './pendingSegmentsFromQueue';

/**
 * Aviso de descarte por tipo (spec 2026-09-28-cola-compound-y-latido § 9).
 * Antes solo se distinguía `action`: una edición de actuación salía como «un
 * accidente». `Record` sobre el tipo: uno nuevo sin texto no compila.
 */
const DISCARD_MESSAGE_KEYS: Record<OfflineCompoundOperation['type'], string> = {
  action: 'common:offline.discarded.action',
  'action-edit': 'common:offline.discarded.actionEdit',
  accident: 'common:offline.discarded.accident',
  'accident-edit': 'common:offline.discarded.accidentEdit',
  communication: 'common:offline.discarded.communication',
};

/**
 * Clave i18n, no el texto: la traduce el banner. La lógica offline no carga
 * `src/i18n`, que en Jest arranca XHR y tumba la CI de `unit` con todo verde.
 */
export function compoundDiscardMessageKey(type: OfflineCompoundOperation['type']): string {
  return DISCARD_MESSAGE_KEYS[type];
}

/**
 * Sync all pending compound offline operations (actions + accidents).
 * Called from OfflineBanner when coming back online.
 */
export async function syncCompoundQueue(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  opts?: { resetFailed?: boolean }
): Promise<{ synced: number; failed: number }> {
  // «Sincronizar ahora»: las agotadas vuelven a intentarlo.
  if (opts?.resetFailed) await resetExhaustedCompoundOperations();
  const operations = await getCompoundQueue();
  if (operations.length === 0) return { synced: 0, failed: 0 };
  const owner = currentQueueOwner();

  let synced = 0;
  let failed = 0;

  for (const op of operations) {
    if (!isOnlineNow()) break;

    // Lo de otro usuario o tenant se aparca (spec 2026-10-01 § 6.2).
    if (!belongsToCurrentSession(op.owner, owner)) continue;
    // 🔴 La sesión puede cambiar A MITAD del drenado (switchTenant sin recarga,
    // logout + login de otro): lo que queda del dueño anterior saldría con el
    // token nuevo. Se mira la sesión de AHORA antes de reproducir y, si ya no
    // es la suya, se corta el drenado sin gastar intentos ni borrar.
    if (!belongsToCurrentSession(op.owner, currentQueueOwner())) break;

    // Agotó sus intentos con respuestas de error del SERVIDOR (los fallos de
    // red ya no cuentan). Se CONSERVA: el auto-sync la salta y «Sincronizar
    // ahora» (`resetFailed`) la reactiva. Antes se BORRABA con sus fotos.
    if (hasExceededMaxRetries(op)) {
      console.warn(
        `[Offline Sync] Operación ${op.id} agotó sus intentos; se conserva hasta «Sincronizar ahora».`
      );
      if (op.status !== 'failed') await updateCompoundStatus(op.id, 'failed');
      failed++;
      continue;
    }

    try {
      await updateCompoundStatus(op.id, 'syncing');

      await replayOperation(apolloClient, op);

      await removeCompoundOperation(op.id);
      synced++;
      console.log(`[Offline Sync] ✅ ${op.type} sincronizada correctamente (${op.id})`);
    } catch (error: any) {
      // La sesión cambió con el replay en curso (spec § 12.1, § 12.11): la
      // operación es válida y va sellada con su dueño. Se conserva tal cual,
      // sin gastar intento ni reportarlo, y se corta: lo que queda es de la
      // sesión anterior. Lo mismo si su dueño cerró sesión a mitad del replay:
      // sin sesión, un paso puede volver «prohibido» y se DESCARTARÍA como
      // validación (spec 2026-10-12 § 10).
      if (isSessionChangedError(error) || sessionLeftOperation(op.owner)) {
        await updateCompoundStatus(op.id, 'pending', op.retryCount, error?.message);
        failed++;
        break;
      }

      Sentry.captureException(error, {
        extra: { operationId: op.id, type: op.type },
      });

      // GAP 5 (auditoría flap-safety): el patrón viejo trataba CUALQUIER
      // graphQLErrors>0 como poison pill y DESCARTABA la op — pero
      // 'Empty AccessToken'/'Refresh token expire' (sesión caducada a mitad
      // de un replay compound) también llegan como GraphQLError, y la
      // operación en sí es válida. classifyOfflineError (mismo helper que ya
      // usa la cola simple en offlineSyncManager.ts) distingue auth de
      // validación real.
      const kind = classifyOfflineError(error);

      if (kind === 'auth') {
        // No descartar: conservar la op para reintentar tras re-login y
        // PARAR el loop (sin sesión el resto de la cola fallará igual).
        console.warn(
          `[Offline Sync] Error de autenticación al sincronizar ${op.type} (${op.id}). Mantengo la operación y pido re-login.`,
          error
        );
        // Auth no cuenta para el poison budget (espejo de la intención de
        // la cola simple, offlineSyncManager.ts): no incrementamos
        // retryCount, así que hasExceededMaxRetries nunca descarta la op
        // solo por sesión caducada — se conserva hasta el re-login.
        await updateCompoundStatus(op.id, 'failed', op.retryCount, error?.message);
        window.dispatchEvent(
          new CustomEvent('offline-sync-auth-required', {
            detail: { messageKey: 'common:offline.sessionExpired' },
          })
        );
        failed++;
        break;
      }

      if (kind === 'validation') {
        console.error(`[Offline Sync] Operación rechazada permanentemente (${op.id}):`, error);
        await removeCompoundOperation(op.id);
        window.dispatchEvent(
          new CustomEvent('offline-sync-error', {
            detail: { messageKey: compoundDiscardMessageKey(op.type) },
          })
        );
        failed++;
      } else if (isTransportFailure(error)) {
        // Sin respuesta del servidor: red caída o degradada, plazo de fase
        // vencido, proxy caído. No dice NADA de la operación, así que no gasta
        // intento. Antes cada fallo sumaba y al sexto la operación se BORRABA
        // con sus fotos: con cobertura mala bastaban cinco desbloqueos.
        console.warn(
          `[Offline Sync] Sin llegar al servidor para ${op.id}. Se reintentará sin gastar intento.`,
          error
        );
        await updateCompoundStatus(op.id, 'pending', op.retryCount, error?.message);
        failed++;
      } else {
        console.warn(`[Offline Sync] Error temporal para ${op.id}. Reintentando más tarde.`, error);
        await updateCompoundStatus(op.id, 'failed', op.retryCount + 1, error?.message);
        failed++;
      }
    }
  }

  return { synced, failed };
}

/**
 * El formulario de una operación, en memoria: sus claves (sin los campos del
 * FormData ni los params, que no son del almacén) y sus fotos. Es lo que el
 * guardado lee y escribe durante el reintento, en vez de la pantalla.
 */
function replayFormContext(op: OfflineCompoundOperation): FormContext {
  const snapshot = Object.fromEntries(
    Object.entries(op.sessionSnapshots).filter(
      ([key]) => !key.startsWith('_formData_') && !key.startsWith('_params_'),
    ),
  );
  return {
    store: createMemoryFormStore(snapshot),
    images: createMemoryImageSource(op.localImages),
    photoHandoff: compoundPhotoHandoff(op),
  };
}

/**
 * La entrega de las fotos de la operación: si un intento anterior ya las dio a
 * la bandeja, este no las vuelve a dar (spec 2026-10-01-photo-outbox-identity
 * § 10.4 E3). Se anota en la operación persistida y también aquí, por si el
 * mismo intento llegara a pedirla dos veces.
 */
function compoundPhotoHandoff(op: OfflineCompoundOperation): PhotoHandoff {
  let done = op.photosHandedOff === true;
  return {
    get done() {
      return done;
    },
    markDone: async () => {
      done = true;
      await markCompoundPhotosHandedOff(op.id);
    },
  };
}

/**
 * Reproduce una operación con su formulario EN MEMORIA (spec
 * outbox-autocontenido v2 § 5.2): ningún tipo toca ya `sessionStorage` ni las
 * carpetas del formulario, así que no hay claves que apartar, carpetas que
 * vaciar ni fotos que reponer. Sirve igual para las operaciones que ya
 * estaban en la tablet antes de este cambio: la forma de la cola no cambia.
 */
async function replayOperation(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  op: OfflineCompoundOperation
): Promise<void> {
  switch (op.type) {
    case 'communication':
      return syncCommunication(apolloClient, op);
    case 'action':
      return syncAction(apolloClient, op);
    case 'action-edit':
      return syncActionEdit(apolloClient, op);
    case 'accident':
      return syncAccident(apolloClient, op);
    case 'accident-edit':
      return syncAccidentEdit(apolloClient, op);
    default: {
      // Un tipo nuevo sin reintento no compila.
      const unknownType: never = op.type;
      throw new Error(`Tipo de operación compound desconocido: ${String(unknownType)}`);
    }
  }
}

/**
 * Replay the full action creation flow.
 *
 * **C-11.2** (2026-05-05): cascada legacy `CREATE_NEW_ORDER + CREATE_NEW_ACTION
 * + Promise.all([UPDATE_ORDER, UPDATE_ORDER_RELATIONSHIPS])` reemplazada por
 * **una sola `SAVE_ACTION`** atómica. Elimina:
 *  - El `Promise.all` paralelo a la misma Order (riesgo P2034 latente).
 *  - El `.catch(() => {})` que enmascaraba errores de sync de location.
 *  - 3 mutations en cada replay → 1 mutation.
 * El backend `saveActionCreate` ya escribe Order + Action coherentes en `$transaction`.
 *
 * Pasos:
 * 1. SAVE_ACTION → get real orderId + actionId, Apollo cache actualizada vía
 *    `update(cache)` con `prependActionToCache`.
 * 2. Las fotos viajan en la fuente en memoria del contexto (outbox v2).
 * 3. Formulario en memoria con los ids reales (no toca `sessionStorage`).
 * 4. Animals/Action/saveData(op.id, contexto) ejecuta las sub-mutations.
 */
async function syncAction(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  op: OfflineCompoundOperation
): Promise<void> {
  // 1. Read the form data from the snapshot
  const rawActionData = op.sessionSnapshots[formActionNaming.NEW_ACTION_DATA];
  if (!rawActionData) {
    throw new Error('No se encontraron datos del formulario de actuación en el snapshot offline');
  }

  const formData = JSON.parse(decompressSessionStorage(rawActionData));

  let realOrderId: string | undefined;
  let realActionId: string | undefined;
  if (op.existing) {
    // La actuación ya existe (fase 1 con red): ni SAVE_ACTION de creación ni
    // vínculo con el tramo, que se hizo entonces. Solo la fase 2, por estos ids
    // (spec 2026-09-08-fase2-encolada § 2 D2).
    realOrderId = op.existing.orderId;
    realActionId = op.existing.entityId;
  } else {
    // 2. SAVE_ACTION atómica — sustituye CREATE_NEW_ORDER + CREATE_NEW_ACTION +
    // Promise.all([UPDATE_ORDER, UPDATE_ORDER_RELATIONSHIPS]). Backend hereda
    // indexExpedition/indexType del parentOrderId y genera ROID `Ar.*`.
    // El input vive en `saveActionInput.ts` (paridad con la semilla de la básica).
    // Idempotencia offline: `op.id` es estable y persistido en IndexedDB; si el
    // replay se reintenta, el backend devuelve la Action ya creada SIN duplicarla.
    // Si el create se intentó con red y se abortó, su clientOpId manda (D3).
    // Con plazo, como la fase 2 (spec § 11): sin él, con cobertura mala este
    // SAVE_ACTION podía quedarse colgado minutos y con él toda la cola, que va
    // en serie. Si vence, es un fallo de red: la operación sigue en la cola sin
    // gastar intento.
    const phase1 = createPhaseDeadline();
    try {
      const phaseClient = phaseScopedClient(apolloClient, phase1);
      const dataSaveAction = await phaseClient.mutate({
        mutation: SAVE_ACTION,
        variables: {
          input: buildSaveActionInputFromForm(formData, {
            clientOpId: op.createClientOpId ?? op.id,
            parentOrderId: op.parentOrderId,
          }),
        },
        update(cache, { data }) {
          prependActionToCache(cache, data?.saveAction, op.parentOrderId);
        },
      });

      realOrderId = dataSaveAction.data?.saveAction?.order?.id;
      realActionId = dataSaveAction.data?.saveAction?.action?.id;
      if (!realOrderId || !realActionId) {
        throw new Error(
          'SAVE_ACTION offline replay no devolvió order.id/action.id'
        );
      }

      // 2b. Básica del RV: vincular tramo ↔ actuación, ANTES de la fase 2 (mismo
      // orden que el camino online en `createBasicAction`). Fail-open a propósito:
      // si el id sigue siendo sintético (el CreateRouteSegment del tramo no llegó
      // a reproducirse) o el link falla, la actuación se crea igual y queda la
      // pastilla «Vincular» del tramo para recuperarlo. Nunca se pierde una
      // actuación por un vínculo.
      if (op.fromSegmentId && !hasSyntheticId({ id: op.fromSegmentId })) {
        try {
          await phaseClient.mutate({
            mutation: LINK_ORDER_TO_SEGMENT,
            variables: { input: { segmentId: op.fromSegmentId, orderId: realOrderId } },
          });
        } catch (linkErr) {
          console.warn(
            '[SR-AutoLink] linkOrderToSegment failed en el replay, la actuación sigue sin vínculo:',
            linkErr
          );
        }
      }
    } finally {
      phase1.clear();
    }
  }

  // 4. El formulario de la operación EN MEMORIA, con los ids reales: el
  // reintento ya no lo escribe en `sessionStorage`, así que no pisa el
  // formulario abierto (spec outbox-autocontenido v2 § 5.2).
  const updatedFormData = {
    ...formData,
    orderId: realOrderId,
    actionId: realActionId,
    orderParentId: op.parentOrderId,
    orderParentRoid: op.parentOrderRoid,
  };
  delete updatedFormData._isOffline;

  const form = replayFormContext(op);
  form.store.setItem(
    formActionNaming.NEW_ACTION_DATA,
    compressSessionStorage(JSON.stringify(updatedFormData))
  );

  // 5. Call the existing Animals/Action/saveData(), which reads the form
  // y ejecuta las sub-mutations (units, geoPosition, images, etc). Internamente
  // usa SAVE_ACTION (update path) — que es atómico y no causa P2034.
  const { saveData: animalsSaveData } = await import(
    '../pages/main/workOrders/NewAction/Animals/Action/saveData'
  );
  // Idempotencia: pasa op.id estable → el SAVE_ACTION de la fase 2 usa un
  // clientOpId derivado (`${op.id}-action-phase2`) que persiste entre replays,
  // así el fold de imágenes no se re-aplica si la cola re-ejecuta esta op.
  await animalsSaveData(op.id, form);
  // Nada que limpiar: el formulario vivía en memoria.
}

/**
 * Replay the full accident creation flow.
 * Restores sessionStorage + images, then calls the existing saveData.
 */
async function syncAccident(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  op: OfflineCompoundOperation
): Promise<void> {
  // 1-2. El formulario y sus fotos, EN MEMORIA con los datos de la operación:
  // el reintento no escribe ni lee `sessionStorage` ni las carpetas de la
  // pantalla, así que no pisa el formulario abierto (spec outbox-autocontenido
  // v2 § 5.2). Sirve igual para las operaciones que ya estaban en la tablet.
  const form = replayFormContext(op);

  // 3. Build a mock FormData from the captured form fields
  const mockFormData = new FormData();
  mockFormData.set('damages', op.sessionSnapshots['_formData_damages'] || '');
  mockFormData.set(
    'involvedVehiclesSwitch',
    op.sessionSnapshots['_formData_involvedVehiclesSwitch'] || 'false'
  );
  mockFormData.set('vehicles', op.sessionSnapshots['_formData_vehicles'] || '{}');

  // 4. Determine the route type
  const accidentRoute = op.isCampaignParent ? 'campaign' : '';

  // 5. Call the existing saveData which handles the full accident creation flow
  const { saveData: accidentSaveData } = await import(
    '../pages/main/workOrders/AddVehicle/Action/saveData'
  );
  // Idempotencia offline: clientOpId ESTABLE derivado de op.id — si la cola
  // reintenta este replay, el backend hace replay del resultado previo y NO
  // duplica el accidente (espejo del patrón `${op.id}-parent`/`-comm`).
  await accidentSaveData(mockFormData, accidentRoute, {
    // El clientOpId del intento con red, si lo hubo (spec D3): un create
    // abortado que llegó al servidor devuelve lo ya creado en vez de duplicar.
    clientOpId: op.createClientOpId ?? `${op.id}-accident`,
    form,
  });
  // Nada que limpiar: lo que el guardado escribió se quedó en la memoria.
}

/**
 * Replay de la EDICIÓN de un accidente encolada offline. Restaura
 * sessionStorage + imágenes OPFS y re-ejecuta el saveData del editar
 * (lee todo de sessionStorage; no recibe FormData). clientOpId estable
 * derivado de op.id → SAVE_ACCIDENT idempotente entre reintentos.
 */
async function syncAccidentEdit(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  op: OfflineCompoundOperation
): Promise<void> {
  // El formulario y sus fotos, en memoria: no toca la pantalla (outbox v2 § 5.2).
  const { saveData: editAccidentSaveData } = await import(
    '../pages/main/workOrders/EditAccident/DamagesAndVehicles/Action/saveData'
  );
  await editAccidentSaveData({
    clientOpId: `${op.id}-accident-edit`,
    isReplay: true,
    form: replayFormContext(op),
  });
}

/**
 * Replay de la EDICIÓN/validación de una actuación encolada offline. Restaura
 * sessionStorage + imágenes OPFS, reconstruye el FormData capturado
 * (prefijo _formData_) y los params de ruta, y re-ejecuta el saveData del
 * editar con clientOpId estable + isReplay (ver guards en ese fichero).
 */
async function syncActionEdit(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  op: OfflineCompoundOperation
): Promise<void> {
  // El formulario y sus fotos, EN MEMORIA con los datos de la operación: el
  // reintento no escribe ni lee `sessionStorage` ni las carpetas de la
  // pantalla, así que no pisa el formulario abierto (spec outbox-autocontenido
  // v2 § 5.2). Sirve igual para las operaciones que ya estaban en la tablet.
  const form = replayFormContext(op);

  const mockFormData = new FormData();
  Object.entries(op.sessionSnapshots).forEach(([key, value]) => {
    if (key.startsWith('_formData_') && value !== null) {
      mockFormData.set(key.slice('_formData_'.length), value);
    }
  });
  const paramsId = op.sessionSnapshots['_params_id'];
  const params = paramsId ? { id: paramsId } : undefined;

  const { saveData: editActionSaveData } = await import(
    '../pages/main/workOrders/ActionsValidation/Animals/Action/saveData'
  );
  await editActionSaveData(mockFormData, params, {
    clientOpId: `${op.id}-action-edit`,
    isReplay: true,
    form,
  });
  // Nada que limpiar: el formulario vivía en memoria.
}

/**
 * Replay del flujo de Communication encolado offline.
 *
 * El operation guarda los inputs ya construidos (`parentInput` opcional para
 * Tab 0 que crea OT padre nueva, `commInput` siempre). Se ejecutan en serie:
 *
 *  1. Si `parentMutation` ≠ null → SAVE_GENERIC_ORDER o SAVE_CAMPAIGN según
 *     `parentMutation`. Devuelve el `order.id` real del nuevo padre.
 *  2. SAVE_COMMUNICATION con `commInput` (inyectando `parentOrderId` real
 *     si la mutation 1 se ejecutó).
 *
 * La comm no tiene croquis ni imágenes (a diferencia de action/accident),
 * por eso `localImages` siempre es []. La cola individual queda vacía
 * después porque el OfflineQueueLink no se usa en este flujo.
 */
async function syncCommunication(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  op: OfflineCompoundOperation
): Promise<void> {
  const data = op.communicationData;
  if (!data) {
    throw new Error(
      'Operación offline de comunicación sin communicationData en el snapshot'
    );
  }

  let resolvedParentOrderId: string | undefined;

  // Step 1: crear OT padre si aplica (Tab 0 de NewComm/Duplicate)
  if (data.parentMutation === 'SAVE_GENERIC_ORDER' && data.parentInput) {
    const parentResult = await apolloClient.mutate({
      mutation: SAVE_GENERIC_ORDER,
      // Replay de la cola compound: el link no debe encolarlo otra vez.
      context: { skipOfflineQueue: true },
      // Idempotencia offline: clientOpId estable derivado de op.id (sufijo
      // -parent para no colisionar con el de la comm). Reintento del replay no
      // duplica la OT padre.
      variables: { input: { ...data.parentInput, clientOpId: `${op.id}-parent` } },
    });
    resolvedParentOrderId = parentResult.data?.saveGenericOrder?.order?.id;
    if (!resolvedParentOrderId) {
      throw new Error(
        'SAVE_GENERIC_ORDER offline replay no devolvió order.id'
      );
    }
    // Replicar prepend a `genericOrders[]` (helper compartido con saveData).
    prependGenericOrderToCache(
      apolloClient.cache,
      parentResult.data?.saveGenericOrder?.genericOrder,
    );
  } else if (data.parentMutation === 'SAVE_CAMPAIGN' && data.parentInput) {
    const parentResult = await apolloClient.mutate({
      mutation: SAVE_CAMPAIGN,
      // Replay de la cola compound: el link no debe encolarlo otra vez.
      context: { skipOfflineQueue: true },
      variables: { input: { ...data.parentInput, clientOpId: `${op.id}-parent` } },
    });
    resolvedParentOrderId = parentResult.data?.saveCampaign?.order?.id;
    if (!resolvedParentOrderId) {
      throw new Error('SAVE_CAMPAIGN offline replay no devolvió order.id');
    }
    prependCampaignToCache(
      apolloClient.cache,
      parentResult.data?.saveCampaign?.campaign,
    );
  }

  // Step 2: SAVE_COMMUNICATION con parentOrderId real
  const commResult = await apolloClient.mutate({
    mutation: SAVE_COMMUNICATION,
    // Replay de la cola compound: el link no debe encolarlo otra vez.
    context: { skipOfflineQueue: true },
    variables: {
      input: {
        ...data.commInput,
        // Idempotencia offline: sufijo -comm (distinto del -parent de arriba).
        clientOpId: `${op.id}-comm`,
        ...(resolvedParentOrderId && { parentOrderId: resolvedParentOrderId }),
      },
    },
  });
  // Replicar prepend a `communications[]` + push al parent's
  // childExpeditionOrderIDs (helper compartido).
  prependCommunicationToCache(
    apolloClient.cache,
    commResult.data?.saveCommunication,
    resolvedParentOrderId ?? (data.commInput?.parentOrderId as string | undefined),
  );
}
